<template>
  <v-container fluid>
    <h2 class="page-title mb-1">B2C CLTV</h2>
    <p class="page-caption mb-4">{{ subtitle }}</p>

    <!-- Resumen: una fila de tarjetas por línea. -->
    <template v-for="line in lines">
      <v-row :key="line.line" dense class="mb-2">
        <v-col cols="12" sm="6" md="4">
          <CardResumeCompanyDetails
            :titulo="`CLTV · ${lineLabel(line)}`"
            :firstValue="formatAmount(line.cltv)"
            :secondValue="lifetimeLabel(line)"
            :showCsvButton="false"
            stacked
          />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <CardResumeCompanyDetails
            :titulo="`Churn mensual · ${lineLabel(line)}`"
            :firstValue="formatRate(line.monthlyChurnRate)"
            :showCsvButton="false"
            stacked
          >
            <!-- El churn se abre por contrato: es la cifra que hay que poder
                 verificar, sobre todo por los recambios que no cuentan. -->
            <template v-slot:second-value>
              <a
                class="drill-link"
                href="#"
                @click.prevent="openChurn(line)"
              >{{ churnDetail(line) }}</a>
            </template>
          </CardResumeCompanyDetails>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <CardResumeCompanyDetails
            :titulo="`CAC · ${lineLabel(line)}`"
            :firstValue="formatAmount(line.cac)"
            :secondValue="cacDetail(line)"
            :showCsvButton="false"
            stacked
          />
        </v-col>
      </v-row>
    </template>

    <!-- Gasto de adquisición cargado a mano, a la vista como en B2B CLTV. -->
    <v-card outlined class="filters-card mt-6 pa-4">
      <div class="d-flex align-center mb-3">
        <h3 class="section-title">Gasto de adquisición</h3>
        <v-spacer></v-spacer>
        <v-select
          v-model="year"
          :items="yearOptions"
          label="Año"
          dense
          hide-details
          outlined
          class="year-select"
          @change="loadData"
        ></v-select>
      </div>

      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model.number="form.b2c"
            label="Gasto de adquisición B2C"
            prefix="USD"
            type="number"
            min="0"
            dense
            hide-details
            outlined
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model.number="form.b2b2c"
            label="Gasto de adquisición B2B2C"
            prefix="USD"
            type="number"
            min="0"
            dense
            hide-details
            outlined
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row dense justify="end" class="mt-2">
        <v-col cols="12" md="auto">
          <v-btn
            color="primary"
            block
            :loading="saving"
            :disabled="!isFormValid || loadingData"
            @click="saveParameters"
          >
            Guardar
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-alert
      v-for="warning in warningMessages"
      :key="warning"
      type="warning"
      dense
      text
      class="mt-4 mb-0"
    >
      {{ warning }}
    </v-alert>

    <!-- Detalle: hace auditable cada cifra de las tarjetas. -->
    <v-data-table
      :headers="headers"
      :items="lines"
      :loading="loadingData"
      :items-per-page="-1"
      hide-default-footer
      class="mt-6"
    >
      <template v-slot:item.line="{ item }">{{ lineLabel(item) }}</template>
      <template v-slot:item.mrrUSD="{ item }">
        {{ formatAmount(item.mrrUSD) }}
      </template>
      <template v-slot:item.arpuMonthlyUSD="{ item }">
        {{ formatAmount(item.arpuMonthlyUSD, 2) }}
      </template>
      <template v-slot:item.monthlyChurnRate="{ item }">
        <a class="drill-link" href="#" @click.prevent="openChurn(item)">{{
          churnLabel(item)
        }}</a>
      </template>
      <template v-slot:item.exposureMonths="{ item }">
        {{ formatNumber(item.exposureMonths) }}
      </template>
      <template v-slot:item.expectedLifetimeMonths="{ item }">
        {{ formatMonths(item.expectedLifetimeMonths) }}
      </template>
      <template v-slot:item.cltv="{ item }">{{ formatAmount(item.cltv) }}</template>
      <template v-slot:item.acquisitionCostUSD="{ item }">
        {{ formatAmount(item.acquisitionCostUSD) }}
      </template>
      <template v-slot:item.cac="{ item }">{{ formatAmount(item.cac) }}</template>
      <template v-slot:item.cltvCacRatio="{ item }">
        {{ formatRatio(item.cltvCacRatio) }}
      </template>
    </v-data-table>

    <p class="footnote mt-4">
      Una membresía es un cliente. B2C suma Directo y Brokers; B2B2C suma
      Planilla y Pago Directo; Upsell y Loyalty quedan fuera, igual que en MRR
      B2C / B2B2C. El churn es neto: bajas menos reactivaciones, sobre los
      meses-membresía activos del período. No cuenta como baja la de una
      mascota que recibe otra membresía dentro de 7 días (recambios de carga y
      cambios al plan 2026). El historial es confiable desde el 1 de julio de
      2026. El CLTV se calcula sobre ingresos, sin descontar costos de
      servicio.
    </p>

    <!-- Detalle del churn de una línea, por contrato. -->
    <v-dialog v-model="showChurn" max-width="800">
      <v-card>
        <v-card-title class="d-flex align-center">
          <span class="dialog-title">{{ churnTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="showChurn = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <p class="footnote mb-3">
            Bajas y reactivaciones del período, agrupadas por el contrato de la
            membresía. Los recambios son bajas que no cuentan: la misma mascota
            recibió otra membresía dentro de 7 días.
          </p>

          <v-data-table
            :headers="churnHeaders"
            :items="churnRows"
            :items-per-page="-1"
            hide-default-footer
            dense
            no-data-text="No hubo bajas ni reactivaciones en el período."
          >
            <template v-slot:item.contractCode="{ item }">
              {{ item.contractCode || 'Sin contrato' }}
            </template>
            <template v-slot:item.companyName="{ item }">
              {{ item.companyName || 'App y Felix' }}
            </template>
            <template v-slot:body.append>
              <tr v-if="churnLine" class="total-row">
                <td colspan="2">Total</td>
                <td>{{ churnLine.churns }}</td>
                <td>{{ churnLine.reactivations }}</td>
                <td>{{ churnLine.replacements }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { utilsMixin } from '../mixins/utilsMixin';
import CardResumeCompanyDetails from '../components/CardResumeCompanyDetails';
import B2cCltvService from '../services/b2c-cltv.service';

/** Primer año con historial de estados y clasificación de MRR B2C / B2B2C. */
const FIRST_YEAR = 2026;

const LINE_LABELS = { b2c: 'B2C', b2b2c: 'B2B2C' };

/** Traducción de los códigos de advertencia que devuelve el backend. */
const WARNING_LABELS = {
  PARAMETERS_NOT_LOADED: 'no tiene gasto de adquisición cargado para el año',
  NO_NEW_MEMBERSHIPS: 'no registra membresías nuevas en el año, así que no hay CAC',
  NO_ACTIVE_MEMBERSHIPS: 'no tiene membresías activas, así que no hay ingreso promedio ni CLTV',
  CAC_IS_ZERO: 'tiene el gasto de adquisición en 0, así que no hay ratio'
};

export default {
  name: 'B2cCltv',
  components: { CardResumeCompanyDetails },
  mixins: [utilsMixin],
  data() {
    const currentYear = new Date().getFullYear();
    return {
      year: currentYear,
      currentYear,
      lines: [],
      window: null,
      loadingData: false,
      saving: false,
      showChurn: false,
      churnLineKey: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: '',
      form: { b2c: null, b2b2c: null },
      headers: [
        { text: 'Línea', value: 'line' },
        { text: 'Membresías activas', value: 'activeMemberships' },
        { text: 'MRR actual', value: 'mrrUSD' },
        { text: 'Ingreso promedio mensual', value: 'arpuMonthlyUSD' },
        { text: 'Churn mensual', value: 'monthlyChurnRate' },
        { text: 'Bajas', value: 'churns' },
        { text: 'Reactivaciones', value: 'reactivations' },
        { text: 'Recambios', value: 'replacements' },
        { text: 'Meses-membresía', value: 'exposureMonths' },
        { text: 'Vida esperada', value: 'expectedLifetimeMonths' },
        { text: 'CLTV', value: 'cltv' },
        { text: 'Nuevas en el año', value: 'newMemberships' },
        { text: 'Gasto adquisición', value: 'acquisitionCostUSD' },
        { text: 'CAC', value: 'cac' },
        { text: 'CLTV / CAC', value: 'cltvCacRatio' }
      ],
      churnHeaders: [
        { text: 'Contrato', value: 'contractCode' },
        { text: 'Empresa', value: 'companyName' },
        { text: 'Bajas', value: 'churns' },
        { text: 'Reactivaciones', value: 'reactivations' },
        { text: 'Recambios', value: 'replacements' }
      ]
    };
  },
  computed: {
    yearOptions() {
      const years = [];
      for (let year = this.currentYear; year >= FIRST_YEAR; year -= 1) years.push(year);
      return years;
    },
    subtitle() {
      if (!this.window) return 'Valores en USD';
      return `Churn medido del ${this.window.from} al ${this.window.to} · valores en USD`;
    },
    churnLine() {
      return this.lines.find((line) => line.line === this.churnLineKey) || null;
    },
    churnRows() {
      return this.churnLine ? this.churnLine.byContract : [];
    },
    churnTitle() {
      if (!this.churnLine) return '';
      return `Churn ${this.year} · ${this.lineLabel(this.churnLine)}`;
    },
    /** Una frase por advertencia, con la línea como sujeto. */
    warningMessages() {
      return this.lines.flatMap((line) =>
        (line.warnings || [])
          .filter((code) => WARNING_LABELS[code])
          .map((code) => `${this.lineLabel(line)} ${WARNING_LABELS[code]}.`)
      );
    },
    /** Los montos que se guardan: solo las líneas con un valor escrito. */
    filledParameters() {
      return ['b2c', 'b2b2c']
        .filter((line) => this.form[line] !== null && this.form[line] !== '')
        .map((line) => ({ line, acquisitionCostUSD: this.form[line] }));
    },
    /** Espejo de la validación del backend, para no depender del 400. */
    isFormValid() {
      return (
        this.filledParameters.length > 0 &&
        this.filledParameters.every(
          ({ acquisitionCostUSD }) =>
            Number.isFinite(acquisitionCostUSD) && acquisitionCostUSD >= 0
        )
      );
    }
  },
  async created() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      this.loadingData = true;
      const res = await B2cCltvService.getMetrics(this.year);
      if (res.status === 'ok') {
        this.lines = res.data.lines;
        this.window = res.data.window;
        this.syncFormFromLines(res.data.lines);
      } else {
        this.lines = [];
        this.window = null;
        this.showErrorSnackbar('Error al cargar los datos de B2C CLTV');
      }
      this.loadingData = false;
    },

    /** Rellena los inputs con lo que hay guardado para el año elegido. */
    syncFormFromLines(lines) {
      const costOf = (key) => {
        const line = lines.find((item) => item.line === key);
        return line ? line.acquisitionCostUSD : null;
      };
      this.form = { b2c: costOf('b2c'), b2b2c: costOf('b2b2c') };
    },

    async saveParameters() {
      this.saving = true;
      const res = await B2cCltvService.saveParameters(this.year, this.filledParameters);
      this.saving = false;

      if (res.status !== 'ok') {
        // Los inputs quedan como están, para reintentar sin volver a escribir.
        this.showErrorSnackbar('Error al guardar el gasto de adquisición');
        return;
      }

      this.showSuccessSnackbar('Gasto de adquisición guardado');
      await this.loadData();
    },

    openChurn(line) {
      this.churnLineKey = line.line;
      this.showChurn = true;
    },

    lineLabel(line) {
      return LINE_LABELS[line.line] || line.line;
    },

    formatAmount(value, digits = 0) {
      if (value === null || value === undefined) return '—';
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      }).format(Number(value));
    },

    formatNumber(value) {
      if (value === null || value === undefined) return '—';
      return new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(
        Number(value)
      );
    },

    formatRate(rate) {
      if (rate === null || rate === undefined) return '—';
      return `${(Number(rate) * 100).toFixed(2)}%`;
    },

    formatRatio(ratio) {
      if (ratio === null || ratio === undefined) return '—';
      return `${Number(ratio).toFixed(2)}x`;
    },

    formatMonths(months) {
      if (months === null || months === undefined) return '—';
      const rounded = Math.round(Number(months));
      return `${rounded} ${rounded === 1 ? 'mes' : 'meses'}`;
    },

    /**
     * Churn con lo que lo respalda. Un supuesto mostrado igual que una
     * medición se lee como medido.
     */
    churnLabel(line) {
      const rate = this.formatRate(line.monthlyChurnRate);
      if (line.churnIsAssumed) return `${rate} · supuesto`;
      return `${rate} · ${this.netLosses(line)}`;
    },

    churnDetail(line) {
      const assumed = line.churnIsAssumed ? 'supuesto · ' : '';
      return `${assumed}${this.netLosses(line)} · ${line.replacements} recambios`;
    },

    netLosses(line) {
      const net = line.churns - line.reactivations;
      return `${net} ${net === 1 ? 'baja neta' : 'bajas netas'}`;
    },

    lifetimeLabel(line) {
      if (line.expectedLifetimeMonths === null) return 'vida esperada sin definir';
      const months = this.formatMonths(line.expectedLifetimeMonths);
      const arpu = this.formatAmount(line.arpuMonthlyUSD, 2);
      return `${arpu}/mes · vida esperada ${months}`;
    },

    cacDetail(line) {
      const ratio = this.formatRatio(line.cltvCacRatio);
      return `${line.newMemberships} nuevas en ${this.year} · CLTV/CAC ${ratio}`;
    }
  }
};
</script>

<style scoped>
.page-title {
  color: var(--pw-primary);
  font-weight: 700;
}
.page-caption {
  color: var(--pw-text-muted);
  font-size: 0.875rem;
  margin-bottom: 0;
}
.section-title {
  color: var(--pw-text);
  font-weight: 600;
  font-size: 14px;
}
.filters-card {
  background-color: var(--pw-surface);
}
.year-select {
  max-width: 120px;
}
.drill-link {
  white-space: nowrap;
  color: inherit;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
  cursor: pointer;
}
.drill-link:hover {
  color: var(--pw-primary);
  text-decoration-style: solid;
}
.dialog-title {
  color: var(--pw-text);
  font-weight: 600;
  font-size: 1rem;
}
.footnote {
  color: var(--pw-text-muted);
  font-size: 0.75rem;
  line-height: 1.5;
}
.total-row {
  font-weight: 600;
}
</style>
